import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRefetchOnAliveUpdate, type PullRequestAliveData} from './use-refetch-on-alive-update'
import {useInvalidateOnAliveUpdate} from './use-invalidate-on-alive-update'
import {useHeaderPageData, useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import type {AliveChannels} from '../page-data/payloads/header'

type HeaderLiveUpdateChannels = {
  pullRequest: AliveChannels['pullRequest']
  pullRequestState: AliveChannels['pullRequestState']
}

const pullRequestUpdateFilter = (data: PullRequestAliveData) =>
  Boolean(data.event_updates?.git_updated) || Boolean(data.event_updates?.title_updated)

export function useHeaderLiveUpdates({pullRequest, pullRequestState}: HeaderLiveUpdateChannels): void {
  const {refetch} = useHeaderPageData()
  const queryKey = useHeaderPageDataQueryKey()
  const dedupeConcurrentRefetches = isFeatureEnabled('merge_status_checks_refetch_dedupe')

  // Original refetch path. Runs when the dedupe flag is OFF; an empty channel makes
  // `useAlive` a no-op when the flag is ON, so this path drops out without a
  // conditional hook call.
  useRefetchOnAliveUpdate(dedupeConcurrentRefetches ? '' : pullRequest, refetch, {
    git_updated: true,
    title_updated: true,
  })
  useRefetchOnAliveUpdate(dedupeConcurrentRefetches ? '' : pullRequestState, refetch)

  // Additive dedupe path. Runs when the flag is ON; empty channel no-ops it when OFF.
  // Intentional: no watchFor on pullRequestState. A pull request state transition
  // (draft -> ready, open/close/merge) can change many header fields, so refetch on
  // ANY pullRequestState event. Do not scope this down.
  useInvalidateOnAliveUpdate(dedupeConcurrentRefetches ? pullRequest : '', queryKey, {filter: pullRequestUpdateFilter})
  useInvalidateOnAliveUpdate(dedupeConcurrentRefetches ? pullRequestState : '', queryKey)
}
